import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Chip, Grid, Typography } from '@mui/material';
import withLayout from '../../hocs/Layout';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PageTitle from '../../common/PageTitle';
import PageHero from '../../common/PageHero';
import MUIModal from '../../common/MUIModal';
import KpiCard from './shared/KpiCard';
import PillFilters from './shared/PillFilters';
import FilterBar from './shared/FilterBar';
import { PILL_KEYS, buildPillFilter, getDefaultDateRange } from './shared/constants';
import useAppointmentSearch from './shared/useAppointmentSearch';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { buildAppointmentColumns } from './shared/columns';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import ExportButton from './shared/ExportButton';
import AppointmentDelivery from '../orders/list2/AddAppDelivery';
import MarkMissedModal from './modals/MarkMissedModal';
import DocumentsModal from '../orders/DocumentsModal';
import { abdTodayFilter } from '../ptl-visibility/constants';
import { formatDate } from '../../helpers/UtilityHelper';

const PILLS = [
    { key: PILL_KEYS.ALL, label: 'All' },
    { key: PILL_KEYS.ACTIVE, label: 'Active' },
    { key: PILL_KEYS.MISS_1, label: '1 Miss' },
    { key: PILL_KEYS.MISS_2, label: '2 Misses' },
    { key: PILL_KEYS.MISS_3_PLUS, label: '3+ Misses', danger: true },
    { key: PILL_KEYS.NEEDS_SCHEDULING, label: 'Needs Scheduling' },
    { key: PILL_KEYS.DELIVERED, label: 'Delivered' },
];

function AllAppointments() {
    const def = getDefaultDateRange();
    const navigate = useNavigate();
    // ?scheduled=today (from the PTL Delivery Health card): appointments scheduled today, any AWB date, so the
    // count here matches the card. The today filter goes into both the list and the dashboard counts.
    const [searchParams] = useSearchParams();
    const scheduledToday = searchParams.get('scheduled') === 'today';
    const [now] = useState(() => new Date());
    const todayFilter = scheduledToday ? abdTodayFilter(now) : [];
    const defaultFilters = scheduledToday ? {} : { fromDate: def.fromDate, toDate: def.toDate };
    const [filters, setFilters] = useState(defaultFilters);
    const [pill, setPill] = useState(PILL_KEYS.ALL);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);

    const [scheduleRow, setScheduleRow] = useState(null);
    const [missRow, setMissRow] = useState(null);
    const [apptDocsRow, setApptDocsRow] = useState(null);

    const extraAndFilter = [...todayFilter, ...buildPillFilter(pill)];
    const { data: searchData, isFetching } = useAppointmentSearch({
        filters, extraAndFilter, page, sizePerPage, reload,
    });
    const { data: kpi, loading: kpiLoading } = useAppointmentDashboard({
        filters, reload, include: ['buckets', 'summary'], extraBaseline: todayFilter,
    });
    const rangeSub = scheduledToday ? 'Appointment today' : 'In selected date range';
    const clearScheduledToday = () => {
        setFilters({ fromDate: def.fromDate, toDate: def.toDate });
        setPage(0);
        navigate('/appointments/all', { replace: true });
    };

    const counts = {
        [PILL_KEYS.ALL]: kpi.total,
        [PILL_KEYS.ACTIVE]: kpi.active,
        [PILL_KEYS.MISS_1]: kpi.miss1,
        [PILL_KEYS.MISS_2]: kpi.miss2,
        [PILL_KEYS.MISS_3_PLUS]: kpi.miss3plus,
        [PILL_KEYS.NEEDS_SCHEDULING]: kpi.needsScheduling,
        [PILL_KEYS.DELIVERED]: kpi.delivered,
    };

    const triggerReload = () => setReload((r) => r + 1);

    const columns = buildAppointmentColumns({
        onSchedule: (row) => setScheduleRow(row),
        onMarkMissed: (row) => setMissRow(row),
        onAddDocs: (row) => setApptDocsRow(row),
    });

    return (
        <div id='main-content' className='container-fluid' style={{ fontFamily: 'Poppins, sans-serif' }}>
            <PageTitle>All Appointments Based Deliveries</PageTitle>
            {/* <PageHero title='All Appointments' subtitle='Track and manage every appointment-based delivery' sx={{ mb: 2.5 }} /> */}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={2.4}>
                    <KpiCard loading={kpiLoading} color='blue' label='Total Appointment Shipments' value={kpi.total}
                        sub={rangeSub} icon={<EventNoteIcon />} selected />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard loading={kpiLoading} color='green' label='Active Appointments' value={kpi.active}
                        sub='Currently scheduled' icon={<EventAvailableIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard loading={kpiLoading} color='red' label='Needs Scheduling' value={kpi.needsScheduling}
                        sub='Waiting for next appointment' icon={<ErrorOutlineIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard loading={kpiLoading} color='teal' label='Delivered' value={kpi.delivered}
                        sub='Successfully delivered' icon={<DoneAllIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard loading={kpiLoading} color='amber' label='Prozo Attributable Misses' value={kpi.prozoFault}
                        sub={rangeSub} icon={<ReportProblemIcon />} />
                </Grid>
            </Grid>

            {scheduledToday && (
                <Chip
                    size='small'
                    color='primary'
                    variant='outlined'
                    label={`Scheduled today · ${formatDate(now, 'dd MMM yyyy')}`}
                    onDelete={clearScheduledToday}
                    sx={{ mb: 1.5, fontWeight: 600 }}
                />
            )}

            <PillFilters pills={PILLS} value={pill} onChange={(k) => {
                setPill(k);
                setPage(0);
                if (k === PILL_KEYS.DELIVERED && filters.missCategory) {
                    setFilters((f) => ({ ...f, missCategory: '' }));
                }
            }} counts={counts} />

            <FilterBar
                key={scheduledToday ? 'today' : 'range'}
                show={scheduledToday
                    ? ['courier', 'awb', 'clientOrderId', 'city', 'attempts', 'missCategory', 'documents']
                    : ['courier', 'awb', 'clientOrderId', 'city', 'attempts', 'missCategory', 'documents', 'dateRange']}
                initial={scheduledToday ? { fromDate: null, toDate: null } : {}}
                onApply={(f) => {
                    setFilters(scheduledToday ? { ...f, fromDate: null, toDate: null } : f);
                    setPage(0);
                    triggerReload();
                }}
                onReset={() => { setFilters(defaultFilters); setPage(0); triggerReload(); }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, pt: 1.5, mt: -0.5, borderTop: '1px solid #eee' }}>
                <Typography variant='caption' fontWeight={600} sx={{ color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Download:</Typography>
                <ExportButton
                    label='Export All (Excel)'
                    filters={filters}
                    extraAndFilter={extraAndFilter}
                    fileName='ABD_All_Appointments'
                />
                <ExportButton
                    label='Download Missed'
                    filters={filters}
                    extraAndFilter={[...todayFilter, { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }]}
                    fileName='ABD_Missed_Appointments'
                    buttonStyle='contained'
                    bgColor='#e65100'
                />
                <ExportButton
                    label='Needs Scheduling'
                    filters={filters}
                    extraAndFilter={[...todayFilter, ...buildPillFilter(PILL_KEYS.NEEDS_SCHEDULING)]}
                    fileName='ABD_Needs_Scheduling'
                    buttonStyle='contained'
                    bgColor='#1565c0'
                />
                <Typography variant='caption' sx={{ color: '#aaa', ml: 'auto', fontSize: 10 }}>
                    Downloads reflect current filters
                </Typography>
            </Box>

            <ExpandableAppointmentsTable
                columns={columns}
                rows={searchData?.data?.length > 0 ? searchData.data : []}
                page={page}
                sizePerPage={sizePerPage}
                setPage={setPage}
                setSizePerPage={setSizePerPage}
                rowCount={-1}
                getRowId={(row) => row.id}
                loading={isFetching}
            />

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1'>
                        {scheduleRow?.appointmentDelivery ? 'Reschedule' : 'Schedule'} Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <AppointmentDelivery rowData={scheduleRow} setDelConfirm={() => setScheduleRow(null)} setReload={triggerReload} />
            </MUIModal>

            <MUIModal
                open={!!missRow}
                handleClose={() => setMissRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1'>
                        Mark as Missed — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{missRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <MarkMissedModal
                    rowData={missRow}
                    onClose={() => setMissRow(null)}
                    onDone={triggerReload}
                    onScheduleNext={(row) => setScheduleRow(row)}
                />
            </MUIModal>

            {/* Same pop-up as the shipments list's "Add Documents to Appointment" (Admin Panel places it on this row too). */}
            <MUIModal
                open={!!apptDocsRow}
                handleClose={() => setApptDocsRow(null)}
                maxWidth='md'
                hideClose={true}
                title={
                    <Typography variant='subtitle1' sx={{ mt: 1 }}>
                        Add Documents to Appointment - AWB{' : '}
                        <Typography component='span' color='error' fontWeight='bold'>{apptDocsRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <DocumentsModal rowData={apptDocsRow} setOpen={() => setApptDocsRow(null)} setReload={triggerReload} />
            </MUIModal>
        </div>
    );
}

export default withLayout(AllAppointments);
